import { storeToRefs } from "pinia"
import { computed, inject } from "vue"
import { useFavicon, useTitle } from "@vueuse/core"
import { useCookies } from "@vueuse/integrations/useCookies"
import { useTheme } from "@af-lib/vue-ui"

/* STORES */
import { useConfigStore } from "@/stores/config"
import { useUserStore } from "@/stores/user"

/* TYPES */
import type { Translate } from "@/plugins/i18n/types"

/* CONSTANTS */
import { COOKIES } from "@/config/constants"

/* UTILS */
import { hexToHSL, hexToRgb } from "@/lib/utils"

type BrandingStyle = Record<string, string>
type HslAdjustments = Parameters<typeof hexToHSL>[1]

const DEFAULT_COLORS = {
  standard: {
    primary: "#ff2f92",
    primaryHover: "#e6006d",
    secondary: "#f59f00",
    border: "#ffd5e9",
  },
  reseller: {
    primary: "#455C51",
    primaryHover: "#455C51",
    secondary: "#f5f0e8",
    border: "#f1f3f4",
  },
} as const

const RGB_OPACITIES = [0.1, 0.2, 0.3, 0.5, 0.65] as const

const toHsl = (color: string, adjustments?: HslAdjustments) =>
  `hsl(${hexToHSL(color, adjustments)})`

export const useBranding = () => {
  const configStore = useConfigStore()
  const userStore = useUserStore()
  const { isResellerAcc } = storeToRefs(userStore)

  const t = inject<Translate>("t")

  const cookies = useCookies()
  const authSession = cookies.get(COOKIES.AUTH_SESSION)

  const branding = computed(
    () => authSession?.branding ?? cookies.get(COOKIES.BRANDING)
  )

  const icon = useFavicon()
  const title = useTitle()

  const defaultColors = computed(() =>
    isResellerAcc.value ? DEFAULT_COLORS.reseller : DEFAULT_COLORS.standard
  )

  // Update aflib primary color
  useTheme({
    primary: authSession?.branding
      ? (branding.value?.color?.cta ?? defaultColors.value.primary)
      : null,
  })

  const colors = computed(() => {
    const defaults = defaultColors.value
    const currentBranding = branding.value

    return {
      cta: currentBranding?.color?.cta ?? defaults.primary,
      ctaHover: currentBranding?.color?.cta ?? defaults.primaryHover,
      theme: currentBranding?.color?.theme ?? defaults.primary,
      gradientStart:
        currentBranding?.background_colors?.primary ?? defaults.primary,
      gradientEnd:
        currentBranding?.background_colors?.secondary ?? defaults.secondary,
      border: currentBranding?.color?.cta ?? defaults.border,
    }
  })

  const style = computed<BrandingStyle>(() => {
    const currentBranding = branding.value
    const { cta, ctaHover, theme, gradientStart, gradientEnd, border } =
      colors.value

    const gradientOpacityProperties: BrandingStyle = {}

    for (const opacity of RGB_OPACITIES) {
      const percentage = opacity * 100

      gradientOpacityProperties[`--af-gradient-start-rgb-${percentage}`] =
        `rgba(${hexToRgb(gradientStart, opacity)})`

      gradientOpacityProperties[`--af-gradient-end-rgb-${percentage}`] =
        `rgba(${hexToRgb(gradientEnd, opacity)})`
    }

    return {
      "--color-background-button-primary-default": toHsl(cta),
      "--color-background-button-primary-disabled": toHsl(cta, {
        lightness: 20,
      }),
      "--color-background-button-primary-hover": toHsl(ctaHover, {
        lightness: -10,
      }),
      "--color-background-button-primary-pressed": toHsl(cta, {
        lightness: -12,
        saturation: -15,
      }),

      "--af-branding-cta": toHsl(cta),
      "--af-branding-cta-hover": toHsl(ctaHover, {
        lightness: -10,
      }),
      "--af-branding-theme": theme,
      "--af-branding-border": border,
      "--af-branding-text": currentBranding?.text_color ?? "#2D2F33",
      "--af-branding-heading": currentBranding?.header_color ?? "#484D51",
      "--af-branding-accent": cta,
      "--af-branding-border-avatar": currentBranding?.color?.theme ?? "#fce2b2",
      "--af-branding-progressbar": currentBranding?.color?.theme ?? "#4ebac7",
      "--af-branding-text-accent": currentBranding?.color?.theme ?? "#029caf",

      "--af-gradient-start": toHsl(gradientStart),
      "--af-gradient-end": toHsl(gradientEnd),
      "--af-gradient-start-rgb": hexToRgb(gradientStart),
      "--af-gradient-end-rgb": hexToRgb(gradientEnd),

      "--bg-checkbox": cta,
      "--color-background-checkbox-checked": cta,
      "--color-icon-primary": cta,

      ...gradientOpacityProperties,

      ...(currentBranding?.background_colors?.primary &&
      currentBranding?.background_colors?.secondary
        ? {
            "--color-af-gradient-gradient-linear": `linear-gradient(
              120deg,
              ${currentBranding.background_colors.primary} 20%,
              ${currentBranding.background_colors.secondary} 70%
            )`,
          }
        : {}),
    }
  })

  const applyBrandingStyle = () => {
    if (typeof document === "undefined") {
      return
    }

    const rootStyle = document.body.style

    for (const [property, value] of Object.entries(style.value)) {
      rootStyle.setProperty(property, value)
    }
  }

  const applyBrandingFavicon = () => {
    const logo = branding.value?.logo

    if (!logo) {
      return
    }

    const cdnUrl = configStore.urls.cdn_legacy.replace(/\/$/, "")
    const logoPath = logo.replace(/^\//, "")

    icon.value = `${cdnUrl}/${logoPath}`
  }

  const applyUnbrandedTitle = () => {
    title.value = t("scan.meta.title_plain")
  }

  return {
    branding,
    style,
    applyBrandingStyle,
    applyBrandingFavicon,
    applyUnbrandedTitle,
  }
}
